import React, { useState } from 'react'
import styled from 'styled-components'
import { ArrowUpRight, Briefcase, Shield, Building2 } from 'lucide-react'
import { Member } from '../../../../Data/member.dto'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../Redux/Hooks'
import { setCurrentMember } from '../../../../Redux/Slice/Dashboard/MemberSlice'
import { IMAGE_CDN } from '../../../../config/config'
import { usePermissions } from '../../../../hooks/usePermissions'

interface MembercardProps {
    member: Member
}

const gradient =
    'linear-gradient(144.01deg, rgba(255, 201, 81, 0.85) 0%, rgba(206, 86, 176, 0.85) 100%)'

function getInitials(name: string) {
    return (
        name
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0]?.toUpperCase())
            .join('') || '?'
    )
}

const MemberCard: React.FC<MembercardProps> = ({ member }) => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { roles } = useAppSelector((state) => state.role)
    const { hasAnyPermission } = usePermissions()
    const canViewRoles = hasAnyPermission(['role_management.read', 'role_management.update'])
    const [imgFailed, setImgFailed] = useState(false)
    const imageSrc = member.profile_image
        ? `${IMAGE_CDN}/${member.profile_image}`
        : ''
    const showImage = Boolean(imageSrc) && !imgFailed

    const assignedRoleName = canViewRoles
        ? (member.role?.name ||
            roles.find((r) =>
                r.assigned_members?.some((m) => String(m.id) === String(member.id))
            )?.name)
        : null

    return (
        <Card
            type="button"
            onClick={() => {
                dispatch(setCurrentMember(member))
                navigate(`/dashboard/members/edit/${member.id}`)
            }}
        >
            <CardGlow aria-hidden />
            <HoverArrow aria-hidden>
                <ArrowUpRight size={15} />
            </HoverArrow>

            <AvatarWrap>
                <AvatarRing>
                    {showImage ? (
                        <Avatar
                            src={imageSrc}
                            alt={member.name}
                            onError={() => setImgFailed(true)}
                        />
                    ) : (
                        <AvatarFallback>{getInitials(member.name || '')}</AvatarFallback>
                    )}
                </AvatarRing>
            </AvatarWrap>

            <Meta>
                <Name>{member.name}</Name>
                <RoleRow>
                    <RoleIcon>
                        <Briefcase size={12} strokeWidth={2.2} />
                    </RoleIcon>
                    <Role>
                        {member.job_type}
                        {(member.job_type || member.employment_type) ? ' · ' : ''}
                        {member.employment_type || 'Full Time'}
                    </Role>
                </RoleRow>
                {assignedRoleName && (
                    <StudioRoleRow title={`Assigned Role: ${assignedRoleName}`}>
                        <Shield size={11} color="#c94daa" />
                        <span>{assignedRoleName}</span>
                    </StudioRoleRow>
                )}
                {member.branch_name && (
                    <BranchRow title={`Branch: ${member.branch_name}`}>
                        <Building2 size={11} color="#e11d48" />
                        <span>{member.branch_name}</span>
                    </BranchRow>
                )}
            </Meta>
        </Card>
    )
}

export default MemberCard

const Card = styled.button`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  margin: 0;
  padding: 22px 18px;
  border: 1px solid rgba(23, 23, 23, 0.07);
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 2px 14px rgba(23, 23, 23, 0.04);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  cursor: pointer;
  overflow: hidden;
  text-align: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

//   &::before {
//     content: '';
//     position: absolute;
//     top: 0;
//     left: 0;
//     right: 0;
//     height: 3px;
//     background: ${gradient};
//     opacity: 0.7;
//   }

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(201, 77, 170, 0.18);
    box-shadow: 0 12px 28px rgba(23, 23, 23, 0.08);
  }
`

const CardGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -40%;
  right: -20%;
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(201, 77, 170, 0.1) 0%, transparent 70%);
`

const HoverArrow = styled.span`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c94daa;
  background: rgba(201, 77, 170, 0.08);
  border: 1px solid rgba(201, 77, 170, 0.14);
  opacity: 0;
  transform: translate(4px, -4px);
  transition: opacity 0.18s ease, transform 0.18s ease;

  ${Card}:hover & {
    opacity: 1;
    transform: translate(0, 0);
  }
`

const AvatarWrap = styled.div`
  position: relative;
  z-index: 1;
`

const AvatarRing = styled.div`
  width: 112px;
  height: 112px;
  border-radius: 50%;
  padding: 4px;
  background: ${gradient};
  box-shadow: 0 10px 28px rgba(201, 77, 170, 0.22);
`

const Avatar = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  border: 3px solid #fff;
  background: #f3f4f6;
  display: block;
`

const AvatarFallback = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Urbanist, sans-serif;
  font-size: 28px;
  font-weight: 800;
  color: #9a4d87;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.22) 0%,
    rgba(201, 77, 170, 0.14) 100%
  );
`

const Meta = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 0;
  width: 100%;
`

const Name = styled.h3`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 17px;
  font-weight: 750;
  line-height: 1.25;
  color: #171717;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: capitalize;
`

const RoleRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(201, 77, 170, 0.06);
  border: 1px solid rgba(201, 77, 170, 0.1);
`

const RoleIcon = styled.span`
  display: inline-flex;
  color: #c94daa;
  flex-shrink: 0;
`

const Role = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.62);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StudioRoleRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 3px 9px;
  border-radius: 999px;
  background: #fdf2f8;
  border: 1px solid rgba(201, 77, 170, 0.2);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const BranchRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 3px 9px;
  border-radius: 999px;
  background: #fff1f2;
  border: 1px solid rgba(225, 29, 72, 0.2);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #e11d48;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

